import { useState, useEffect, useCallback } from 'react'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { API_BASE as API_BASE_CONFIG } from '../../config'

// Use the shared API helpers that handle base URL, auth headers, and CORS
async function apiPost(path, body) {
  // Import dynamically to use the same request wrapper as the rest of the app
  const API_BASE = API_BASE_CONFIG
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    // detail is a string, or {message, mapping_report} when an export failed a sheet
    const detail = typeof err.detail === 'string' ? err.detail : err.detail?.message
    throw new Error(detail || `Request failed: ${res.status}`)
  }
  return res.json()
}

const STATUS_CONFIG = {
  confirmed: { icon: '✅', label: 'Confirmed', cls: 'mp-status-confirmed' },
  auto:      { icon: '🟢', label: 'Auto',      cls: 'mp-status-auto' },
  suggested: { icon: '🟡', label: 'Review',    cls: 'mp-status-suggested' },
  unmapped:  { icon: '❌', label: 'Unmapped',  cls: 'mp-status-unmapped' },
}

const METHOD_LABELS = {
  saved_mapping: 'Saved',
  vendor_schema: 'Vendor Schema',
  vendor_dictionary: 'Vendor Dict',
  exact_name: 'Exact Match',
  normalized_name: 'Normalized',
  fuzzy: 'Fuzzy',
  substring: 'Substring',
  manual: 'Manual',
  unmapped: '—',
}

/* ─── COLUMN ROW ─── */
function ColumnRow({ col, customerColumns, onOverride }) {
  const [editing, setEditing] = useState(false)
  const [search, setSearch] = useState('')
  const sc = STATUS_CONFIG[col.status] || STATUS_CONFIG.unmapped
  const method = METHOD_LABELS[col.method] || col.method

  const filtered = customerColumns.filter(c =>
    c.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className={`mp-col-row ${col.status === 'unmapped' ? 'mp-col-unmapped' : ''}`}>
      <div className="mp-col-template" title={col.template_column}>
        {col.template_column}
      </div>
      <div className="mp-col-arrow">→</div>
      <div className="mp-col-target">
        {editing ? (
          <div className="mp-col-edit">
            <input
              className="mp-col-search"
              placeholder="Search columns…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
            <div className="mp-col-dropdown">
              <div
                className="mp-col-option mp-col-option-none"
                onClick={() => { onOverride(col.template_column, null); setEditing(false) }}
              >
                — Unmapped —
              </div>
              {filtered.slice(0, 30).map(c => (
                <div
                  key={c}
                  className={`mp-col-option ${c === col.customer_column ? 'selected' : ''}`}
                  onClick={() => { onOverride(col.template_column, c); setEditing(false) }}
                >
                  {c}
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="mp-col-option mp-col-no-match">No matching columns</div>
              )}
            </div>
            <button className="mp-col-cancel" onClick={() => setEditing(false)}>✕</button>
          </div>
        ) : (
          <div className="mp-col-value" onClick={() => setEditing(true)}>
            {col.customer_column ? (
              <span className="mp-col-mapped">{col.customer_column}</span>
            ) : (
              <span className="mp-col-empty">Click to map</span>
            )}
            <span className="mp-col-edit-icon">✎</span>
          </div>
        )}
      </div>
      <div className={`mp-col-status ${sc.cls}`}>
        <span className="mp-status-icon">{sc.icon}</span>
        <span className="mp-status-text">{sc.label}</span>
      </div>
      <div className="mp-col-method" title={col.method}>
        {method}
      </div>
      <div className="mp-col-conf">
        {col.confidence > 0 ? `${Math.round(col.confidence * 100)}%` : '—'}
      </div>
    </div>
  )
}

/* ─── SHEET SECTION ─── */
function SheetSection({ sheet, customerColumns, overrides, onOverride }) {
  const [collapsed, setCollapsed] = useState(false)
  const cols = sheet.columns || []
  const confirmed = cols.filter(c => c.status === 'confirmed').length
  const auto = cols.filter(c => c.status === 'auto').length
  const suggested = cols.filter(c => c.status === 'suggested').length
  const unmapped = cols.filter(c => c.status === 'unmapped').length
  const mapped = confirmed + auto + suggested
  const total = cols.length

  // Apply overrides to column status display
  const displayCols = cols.map(c => {
    const key = `${sheet.sheet}::${c.template_column}`
    if (key in overrides) {
      const ov = overrides[key]
      if (ov === null) return { ...c, customer_column: null, status: 'unmapped', method: 'manual', confidence: 0 }
      return { ...c, customer_column: ov, status: 'confirmed', method: 'manual', confidence: 1.0 }
    }
    return c
  })

  return (
    <div className="mp-sheet">
      <div className="mp-sheet-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="mp-sheet-toggle">{collapsed ? '▸' : '▾'}</div>
        <div className="mp-sheet-name">{sheet.sheet}</div>
        <div className="mp-sheet-stats">
          <span className="mp-sheet-total">{total} cols</span>
          {confirmed > 0 && <span className="mp-stat-chip mp-chip-confirmed">✅ {confirmed}</span>}
          {auto > 0 && <span className="mp-stat-chip mp-chip-auto">🟢 {auto}</span>}
          {suggested > 0 && <span className="mp-stat-chip mp-chip-suggested">🟡 {suggested}</span>}
          {unmapped > 0 && <span className="mp-stat-chip mp-chip-unmapped">❌ {unmapped}</span>}
        </div>
        <div className="mp-sheet-bar">
          <div className="mp-bar-fill" style={{ width: `${total > 0 ? (mapped / total) * 100 : 0}%` }} />
        </div>
        <div className="mp-sheet-pct">{total > 0 ? Math.round((mapped / total) * 100) : 0}%</div>
      </div>
      {!collapsed && (
        <div className="mp-sheet-body">
          <div className="mp-col-header-row">
            <div className="mp-col-template">Template Column</div>
            <div className="mp-col-arrow" />
            <div className="mp-col-target">Customer Column</div>
            <div className="mp-col-status">Status</div>
            <div className="mp-col-method">Method</div>
            <div className="mp-col-conf">Conf</div>
          </div>
          {displayCols.map(c => (
            <ColumnRow
              key={c.template_column}
              col={c}
              customerColumns={customerColumns}
              onOverride={(tcol, ccol) => onOverride(sheet.sheet, tcol, ccol)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── MAIN COMPONENT ─── */
export default function MappingPreview({ templateId, templateName, onClose, onExport }) {
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList(list => {
    if (list.length === 1) setCustomerId(list[0].customer_id || list[0].id)
  })
  const [customerId, setCustomerId] = useState('')
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [overrides, setOverrides] = useState({})   // "sheet::tcol" → ccol|null
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Run preview when customer is selected
  const runPreview = useCallback(async () => {
    if (!customerId || !templateId) return
    setLoading(true)
    setError(null)
    setPreview(null)
    setOverrides({})
    setSaved(false)
    try {
      const data = await apiPost('/api/export/from-template/preview', {
        customer_id: customerId,
        template_id: templateId,
        auto_map: true,
      })
      setPreview(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [customerId, templateId])

  useEffect(() => {
    if (customerId) runPreview()
  }, [customerId, runPreview])

  // Collect all customer column names for dropdowns
  const allCustomerCols = []
  // Gather from the mapping results themselves (most reliable source)
  if (preview?.sheets) {
    for (const s of preview.sheets) {
      for (const c of (s.columns || [])) {
        if (c.customer_column && !allCustomerCols.includes(c.customer_column)) {
          allCustomerCols.push(c.customer_column)
        }
      }
    }
  }
  // Also try customer_tables if they have column lists
  if (preview?.customer_tables) {
    for (const t of preview.customer_tables) {
      const cols = t.columns_list || t.column_names || []
      if (Array.isArray(cols)) {
        for (const col of cols) {
          const name = typeof col === 'string' ? col : (col?.name || '')
          if (name && !allCustomerCols.includes(name)) allCustomerCols.push(name)
        }
      }
    }
  }
  allCustomerCols.sort()

  function handleOverride(sheetName, templateCol, customerCol) {
    setOverrides(prev => ({ ...prev, [`${sheetName}::${templateCol}`]: customerCol }))
    setSaved(false)
  }

  // Summary stats
  let totalCols = 0, totalMapped = 0, totalConfirmed = 0, totalAuto = 0, totalSuggested = 0, totalUnmapped = 0
  if (preview?.sheets) {
    for (const s of preview.sheets) {
      totalCols += s.total_columns || 0
      totalConfirmed += s.confirmed || 0
      totalAuto += s.auto_mapped || 0
      totalSuggested += s.suggested || 0
      totalUnmapped += s.unmapped || 0
    }
    totalMapped = totalConfirmed + totalAuto + totalSuggested
  }

  // Adjust for overrides
  const overrideCount = Object.values(overrides).filter(v => v !== null).length
  const unmapCount = Object.values(overrides).filter(v => v === null).length

  async function handleSaveMappings() {
    if (!preview?.sheets) return
    setSaving(true)
    try {
      // Build mappings object from preview + overrides
      const mappings = {}
      for (const s of preview.sheets) {
        const sheetMappings = {}
        for (const c of (s.columns || [])) {
          const key = `${s.sheet}::${c.template_column}`
          const target = key in overrides ? overrides[key] : c.customer_column
          if (target) {
            sheetMappings[c.template_column] = target
          }
        }
        if (Object.keys(sheetMappings).length > 0) {
          mappings[s.sheet] = sheetMappings
        }
      }

      await apiPost('/api/export/from-template/save-mappings', {
        customer_id: customerId,
        template_id: templateId,
        mappings,
        save_as_reusable: true,
      })
      setSaved(true)
    } catch (err) {
      alert('Failed to save mappings: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleExport() {
    if (!preview?.sheets) return
    setExporting(true)
    try {
      // Overrides as the API reads them: column_mappings = {sheet: {template col: customer col}}
      const manual = {}
      for (const [key, val] of Object.entries(overrides)) {
        if (val) {
          const [sheet, tcol] = key.split('::')
          if (!manual[sheet]) manual[sheet] = {}
          manual[sheet][tcol] = val
        }
      }

      const result = await apiPost('/api/export/from-template', {
        customer_id: customerId,
        template_id: templateId,
        auto_map: true,
        column_mappings: Object.keys(manual).length > 0 ? manual : undefined,
      })

      // A sheet the export could not fill is said out loud, never a silent empty tab.
      const problems = (result.mapping_report || []).filter(r => r.status === 'error' || r.status === 'query_error')
      if (problems.length > 0) {
        alert('Some sheets could not be filled:\n' +
              problems.map(r => `${r.sheet}: ${r.error || r.status}`).join('\n'))
      }

      if (onExport) onExport(result)
      else if (result.download_url) {
        const base = API_BASE_CONFIG
        window.open(`${base}${result.download_url}`, '_blank')
      }
    } catch (err) {
      alert('Export failed: ' + err.message)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="mp-overlay">
      <div className="mp-panel">
        {/* Header */}
        <div className="mp-header">
          <div className="mp-header-left">
            <button className="mp-close" onClick={onClose}>←</button>
            <div>
              <div className="mp-title">Column Mapping</div>
              <div className="mp-subtitle">{templateName || templateId}</div>
            </div>
          </div>
          <div className="mp-header-right">
            {preview && (
              <>
                <button
                  className="mp-btn mp-btn-secondary"
                  onClick={handleSaveMappings}
                  disabled={saving || totalMapped === 0}
                >
                  {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Mappings'}
                </button>
                <button
                  className="mp-btn mp-btn-primary"
                  onClick={handleExport}
                  disabled={exporting || totalMapped === 0}
                >
                  {exporting ? 'Exporting…' : `Export (${totalMapped} mapped)`}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Customer Selector */}
        <div className="mp-customer-bar">
          <label className="mp-customer-label">Customer</label>
          <select
            className="mp-customer-select"
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
          >
            <option value="">Select a customer…</option>
            {customers.map(c => (
              <option key={c.customer_id || c.id} value={c.customer_id || c.id}>
                {c.company_name || c.customer_name || c.customer_id || c.id}
              </option>
            ))}
          </select>
          {customersError && <LoadError inline what="customers" error={customersError} onRetry={reloadCustomers} />}
          {preview?.vendor_id && (
            <span className="mp-vendor-badge">
              Vendor: {preview.vendor_id.toUpperCase()}
            </span>
          )}
          {preview && (
            <button className="mp-refresh-btn" onClick={runPreview} title="Re-run mapping">↻</button>
          )}
        </div>

        {/* Content */}
        <div className="mp-content">
          {!customerId && (
            <div className="mp-empty">
              <div className="mp-empty-icon">🔗</div>
              <div className="mp-empty-title">Select a customer</div>
              <div className="mp-empty-text">Choose a customer to preview how their data maps to this template's columns.</div>
            </div>
          )}

          {loading && (
            <div className="mp-empty">
              <div className="mp-spinner" />
              <div className="mp-empty-title">Analyzing columns…</div>
              <div className="mp-empty-text">Matching template columns against {customerId}'s data using vendor dictionaries, saved mappings, and schema intelligence.</div>
            </div>
          )}

          {error && (
            <div className="mp-error">
              <div className="mp-error-icon">⚠</div>
              <div className="mp-error-title">Mapping Preview Failed</div>
              <div className="mp-error-text">{error}</div>
              <button className="mp-btn mp-btn-secondary" onClick={runPreview}>Retry</button>
            </div>
          )}

          {preview && !loading && (
            <>
              {/* Summary Bar */}
              <div className="mp-summary">
                <div className="mp-summary-stat">
                  <div className="mp-summary-value">{totalCols}</div>
                  <div className="mp-summary-label">Total</div>
                </div>
                <div className="mp-summary-stat mp-stat-green">
                  <div className="mp-summary-value">{totalConfirmed}</div>
                  <div className="mp-summary-label">Confirmed</div>
                </div>
                <div className="mp-summary-stat mp-stat-auto">
                  <div className="mp-summary-value">{totalAuto}</div>
                  <div className="mp-summary-label">Auto</div>
                </div>
                <div className="mp-summary-stat mp-stat-amber">
                  <div className="mp-summary-value">{totalSuggested}</div>
                  <div className="mp-summary-label">Review</div>
                </div>
                <div className="mp-summary-stat mp-stat-red">
                  <div className="mp-summary-value">{totalUnmapped}</div>
                  <div className="mp-summary-label">Unmapped</div>
                </div>
                {overrideCount > 0 && (
                  <div className="mp-summary-stat mp-stat-blue">
                    <div className="mp-summary-value">{overrideCount}</div>
                    <div className="mp-summary-label">Overrides</div>
                  </div>
                )}
                <div className="mp-summary-bar-wrap">
                  <div className="mp-summary-bar">
                    <div className="mp-bar-confirmed" style={{ width: `${totalCols > 0 ? (totalConfirmed / totalCols) * 100 : 0}%` }} />
                    <div className="mp-bar-auto" style={{ width: `${totalCols > 0 ? (totalAuto / totalCols) * 100 : 0}%` }} />
                    <div className="mp-bar-suggested" style={{ width: `${totalCols > 0 ? (totalSuggested / totalCols) * 100 : 0}%` }} />
                  </div>
                  <div className="mp-summary-pct">
                    {totalCols > 0 ? Math.round((totalMapped / totalCols) * 100) : 0}% mapped
                  </div>
                </div>
              </div>

              {/* Customer Tables Info */}
              {preview.customer_tables && preview.customer_tables.length > 0 && (
                <div className="mp-tables-info">
                  <span className="mp-tables-label">Data sources:</span>
                  {preview.customer_tables.map(t => (
                    <span key={t.table} className="mp-table-chip">
                      {t.table.replace(`cust_${customerId}_`, '').replace(`${customerId}_`, '')} ({t.columns})
                    </span>
                  ))}
                </div>
              )}

              {/* Sheet Sections */}
              {(preview.sheets || []).map(s => (
                <SheetSection
                  key={s.sheet}
                  sheet={s}
                  customerColumns={allCustomerCols}
                  overrides={overrides}
                  onOverride={handleOverride}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
